<template>
    <div class="home-page">
        <div class="content-container">
            <!-- Left Side: Text, Buttons and What's New -->
            <div class="hero-left">
                <div class="hero-text">
                    <MyStekzLogo class="hero-logo" />
                    <h1 class="hero-title">
                        The superintelligent business runs on its own model
                    </h1>
                    <p class="hero-subtitle">
                        MyStekz turns your business into a living model that
                        people and AI agents operate together. The longer it
                        runs, the smarter it gets.
                    </p>

                    <div class="hero-actions">
                        <router-link to="/signup">
                            <Button
                                label="Create an account"
                                class="p-button-lg create-account-btn"
                            />
                        </router-link>
                        <p class="signin-text">
                            Already have an account?
                            <router-link to="/login">Sign in</router-link>
                        </p>
                    </div>
                </div>

                <!-- What's New section -->
                <div
                    v-if="whatsNewEntries.length > 0"
                    class="whats-new-section"
                >
                    <div class="section-header">
                        <h2>What's New</h2>
                    </div>
                    <div class="whats-new-list">
                        <div
                            v-for="entry in whatsNewEntries"
                            :key="entry.identity"
                            class="whats-new-item"
                            role="button"
                            tabindex="0"
                            @click="openWhatsNewEntry(entry)"
                            @keydown.enter.prevent="openWhatsNewEntry(entry)"
                            @keydown.space.prevent="openWhatsNewEntry(entry)"
                        >
                            <img
                                v-if="entry.image_url"
                                :src="entry.image_url"
                                :alt="entry.title"
                                class="entry-thumbnail"
                            />
                            <div v-else class="entry-thumbnail-placeholder">
                                <i class="pi pi-sparkles" />
                            </div>
                            <div class="entry-content">
                                <div class="entry-header">
                                    <span class="entry-title-text">{{
                                        entry.title
                                    }}</span>
                                    <span class="entry-date">{{
                                        formatEntryDate(entry.created_at)
                                    }}</span>
                                </div>
                                <p class="entry-body-preview">
                                    {{ truncateBody(entry.body) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Right Side: Laptop and Video -->
            <div class="hero-graphic">
                <div class="laptop-container">
                    <img
                        :src="'/assets/Device-1024x620.webp'"
                        alt="Laptop"
                        class="laptop-frame"
                    />
                    <div class="video-overlay">
                        <video
                            autoplay
                            loop
                            muted
                            playsinline
                            class="demo-video"
                        >
                            <source
                                :src="'/assets/Token-Simulation-WMS-Picking-Optimization.mp4'"
                                type="video/mp4"
                            />
                        </video>
                    </div>
                </div>

                <!-- Floating shapes (placeholders for the colorful decorations) -->
                <div class="shape shape-1"></div>
                <div class="shape shape-2"></div>
                <div class="shape shape-3"></div>
                <div class="shape shape-4"></div>
                <div class="shape shape-5"></div>
                <div class="shape shape-6"></div>
            </div>
        </div>

        <!-- Bottom section: Logo Cloud -->
        <div class="logo-cloud-section">
            <h2 class="logo-cloud-title">
                Businesses built intelligently on MyStekz
            </h2>
            <TransitionGroup name="logo-list" tag="div" class="logo-grid">
                <img
                    v-for="logo in logos.slice(0, 5)"
                    :key="logo.src"
                    :src="logo.src"
                    :alt="logo.alt"
                    class="partner-logo"
                />
            </TransitionGroup>
        </div>

        <!-- ISO Certification Footer Item -->
        <div class="footer">
            <div class="iso-box">
                <img
                    :src="'/assets/ISO-certification.webp'"
                    alt="Wovar"
                    class="iso-logo"
                />
                <div class="iso-text">
                    <strong>ISO 27001:2022</strong>
                    <span>Certified by Brand Compliance</span>
                </div>
            </div>
            <router-link to="/docs/"> API Documentation </router-link>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";

import MyStekzLogo from "@/assets/mystekz-logo-white.svg?component";
import { useAuthenticationManager } from "@/stores/authentication-store";
import {
    WHATS_NEW_PREVIEW_LIMIT,
    useWhatsNewManager,
    useWhatsNewStore,
} from "@/stores/whats-new-store";

// Has the side-effect of redirecting you to the correct page when logged in.
useAuthenticationManager();
const whatsNewStore = useWhatsNewStore();
const { entries: allWhatsNewEntries } = useWhatsNewManager();
const whatsNewEntries = computed(() =>
    allWhatsNewEntries.value.slice(0, WHATS_NEW_PREVIEW_LIMIT)
);

// initDialog must be called during setup() so useDialog() has injection context.
whatsNewStore.initDialog();

const logos = ref([
    { src: "/assets/logos/NOM.webp", alt: "NOM" },
    { src: "/assets/logos/am.png", alt: "AM" },
    { src: "/assets/logos/VanVuuren.png", alt: "Van Vuuren" },
    { src: "/assets/logos/GSP.webp", alt: "Groningen Seaports" },
    { src: "/assets/logos/Wovar.webp", alt: "Wovar" },
    { src: "/assets/logos/BPZ.png", alt: "BPZ" },
    { src: "/assets/logos/defibrion.png", alt: "Defibrion" },
]);

let intervalId: ReturnType<typeof setInterval>;

const rotateLogos = () => {
    const first = logos.value.shift();
    if (first) {
        logos.value.push(first);
    }
};

const formatEntryDate = (date: Date | string) => {
    const d = typeof date === "string" ? new Date(date) : date;
    return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
};

const truncateBody = (body: string, maxLen = 100) => {
    if (body.length <= maxLen) return body;
    return body.substring(0, maxLen).trimEnd() + "…";
};

const openWhatsNewEntry = (entry: { identity: string }) => {
    whatsNewStore.openModal(false, entry.identity);
};

onMounted(async () => {
    intervalId = setInterval(rotateLogos, 3000);
    await whatsNewStore.fetchEntries({ limit: WHATS_NEW_PREVIEW_LIMIT });
});

onUnmounted(() => {
    clearInterval(intervalId);
});
</script>

<style scoped lang="scss">
/* Logo Carousel Transitions */
.logo-list-move,
.logo-list-enter-active,
.logo-list-leave-active {
    transition: all 0.8s ease;
}

.logo-list-enter-from {
    opacity: 0;
    transform: translateX(0);
}
.logo-list-leave-to {
    opacity: 0;
    transform: translateX(-30px);
}

.logo-list-leave-active {
    position: absolute;
}

.home-page {
    min-height: 100vh;
    width: 100%;
    background-color: var(--stekz-primary-color); // Deep purple background
    color: var(--stekz-white);
    display: flex;
    flex-direction: column;
    padding: 2rem 4rem;
    overflow-x: hidden;
    position: relative;
}

.content-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1;
    gap: 4rem;
}

.hero-left {
    flex: 1;
    max-width: 500px;
    z-index: 2;
    display: flex;
    flex-direction: column;
    gap: 3rem;
}

.hero-text {
    text-align: center;

    .hero-logo {
        height: 60px;
        margin: auto;
        margin-bottom: 2rem;
    }

    .hero-title {
        font-size: 1.8rem;
        line-height: 1.3;
        font-weight: 800;
        margin-bottom: 0.5rem;
        color: var(--stekz-white);
    }

    .hero-subtitle {
        font-size: 1rem;
        font-weight: 500;
        margin-bottom: 3rem;
        color: var(--stekz-white);
    }
}

.hero-actions {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-width: 300px;
    margin: auto;

    .learn-more-btn,
    .create-account-btn {
        background-color: var(--stekz-secondary-color) !important; // Pink color
        border: none !important;
        color: var(--stekz-white) !important;
        padding: 1rem !important;
        font-weight: 600 !important;
        width: 100%;
    }

    .docs-btn {
        background: transparent !important;
        border: 1px solid
            color-mix(in srgb, var(--stekz-white), transparent 35%) !important;
    }

    .signin-text {
        margin-top: 1rem;
        font-size: 1.1rem;
        color: var(--stekz-white);

        a {
            color: var(--stekz-white);
            text-decoration: underline;
            font-weight: 600;
        }
    }
}

.hero-graphic {
    flex: 1.2;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
}

.laptop-container {
    position: relative;
    width: 100%;
    max-width: 800px;
    z-index: 1;

    .laptop-frame {
        width: 100%;
        height: auto;
        display: block;
    }

    .video-overlay {
        position: absolute;
        top: 3.5%;
        left: 10%;
        width: 80%;
        height: 85%;
        background: black;
        overflow: hidden;

        .demo-video {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }
}

// Decorations
.shape {
    position: absolute;
    width: 100px;
    height: 14px;
}

.shape-1 {
    background: var(--palette-orange-400);
    transform: rotate(-45deg);
    top: -20px;
    right: 30%;
    z-index: 1;
}

.shape-2 {
    background: var(--stekz-secondary-color);
    transform: rotate(30deg);
    top: 20%;
    left: -40px;
}

.shape-3 {
    background: var(--stekz-secondary-color);
    transform: rotate(-30deg);
    bottom: 20%;
    right: -20px;
}

.shape-4 {
    background: var(--palette-green-400);
    transform: rotate(10deg);
    bottom: 5%;
    left: 16%;
    z-index: 1;
}

.shape-5 {
    background: var(--palette-orange-400);
    transform: rotate(45deg);
    bottom: -10px;
    right: 25%;
}

.shape-6 {
    background: var(--stekz-secondary-color);
    transform: rotate(70deg);
    top: 0px;
    right: 54%;
    z-index: 0;
}

.whats-new-section {
    margin-bottom: var(--default-gap);

    .section-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 0.75rem;

        h2 {
            margin: 0;
            font-size: 1.2rem;
            color: var(--stekz-white);
        }
    }

    .whats-new-list {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .whats-new-item {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.75rem;
        padding: 0.5rem;
        background: color-mix(in srgb, var(--stekz-white), transparent 90%);
        color: var(--stekz-white);
        cursor: pointer;
        transition: background-color 0.2s ease;

        &:hover {
            background: color-mix(in srgb, var(--stekz-white), transparent 80%);
        }

        &:focus-visible {
            outline: 2px solid var(--stekz-white);
            outline-offset: 2px;
        }

        .entry-thumbnail,
        .entry-thumbnail-placeholder {
            width: 48px;
            height: 48px;
            flex-shrink: 0;
            object-fit: cover;
            display: block;
        }

        .entry-thumbnail-placeholder {
            display: flex;
            align-items: center;
            justify-content: center;
            background: color-mix(in srgb, var(--stekz-white), transparent 80%);
            font-size: 1.1rem;
        }

        .entry-content {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 0.15rem;
        }

        .entry-header {
            display: flex;
            align-items: baseline;
            gap: 0.5rem;

            .entry-title-text {
                flex: 1;
                font-weight: 600;
                font-size: 0.9rem;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .entry-date {
                flex-shrink: 0;
                font-size: 0.7rem;
                color: color-mix(in srgb, var(--stekz-white), transparent 25%);
            }
        }

        .entry-body-preview {
            margin: 0;
            font-size: 0.8rem;
            color: var(--stekz-white);
            overflow: hidden;
            display: -webkit-box;
            -webkit-line-clamp: 1;
            -webkit-box-orient: vertical;
        }
    }
}

.logo-cloud-section {
    margin-top: 4rem;
    text-align: center;
    width: 67%;
    margin-left: auto;

    .logo-cloud-title {
        font-size: 1.5rem;
        margin-bottom: 2rem;
        font-weight: 500;
        color: var(--stekz-white);
    }

    .logo-grid {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 4rem;
        flex-wrap: nowrap;
        overflow: hidden;
        width: 100%;
        position: relative;
        min-height: 80px;
    }

    .partner-logo {
        height: 50px;
        max-width: 150px;
        object-fit: contain;
        filter: brightness(0) invert(1); // Make logos white to match design
        flex-shrink: 0;
    }

    .partner-logo-placeholder {
        font-size: 1.2rem;
        font-weight: 700;
        opacity: 0.9;
        text-transform: uppercase;
        letter-spacing: 1px;
    }
}

.footer {
    position: absolute;
    bottom: 2rem;
    left: 4rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 2rem;

    > * {
        flex: 1;
    }

    .iso-box {
        display: flex;
        align-items: center;
        gap: 1rem;

        .iso-logo {
            max-width: 256px;
        }

        .iso-text {
            display: none;
        }
    }
}

/* Responsive adjustments */

@media screen and (max-width: 1024px) {
    .home-page {
        padding: 2rem;
    }

    .content-container {
        flex-direction: column;
        text-align: center;
        gap: 2rem;
    }

    .hero-left {
        max-width: 100%;
        align-items: center;
    }

    .hero-text {
        max-width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;

        .hero-logo {
            height: 40px;
        }

        .hero-title {
            font-size: 1.8rem;
        }

        .hero-subtitle {
            font-size: 1.5rem;
        }
    }

    .hero-graphic {
        width: 100%;
    }

    .footer {
        position: static;
        margin-top: 3rem;
        display: flex;
        justify-content: center;
    }
}

@media screen and (max-width: 640px) {
    .home-page {
        padding: 1rem;
    }

    .logo-grid {
        gap: 1.5rem;
    }
}
</style>
